// Guided: add a Data Source (a "data point" of storage_type=file/db). Domain-
// agnostic. Drives the assistant to register a source the user describes, then
// discovers its schema. Gates on the project's data_source count rising.
import { listStorages } from "../../../services/pipeline";

async function countSources() {
  try {
    const r = await listStorages();
    const arr = Array.isArray(r) ? r : (r?.storages || []);
    return arr.filter((s) => {
      const t = (s.storage_type || s.type || "").toLowerCase();
      return t && t !== "postgres" && t !== "mysql" && t !== "mongo" && !/^(postgres|mysql|mongo)/.test(s.storage_id || "");
    }).length;
  } catch { return 0; }
}

// Per-type guidance: the human-facing label, the connection details to gather,
// and the assistant prompt that registers THAT source type. The guide is driven
// by ctx.kind (passed from the New Source → type picker → startGuided).
const KIND_GUIDE = {
  file: {
    label: "File (CSV / XLSX / JSON / Parquet)",
    desc: "an uploaded file (CSV, XLSX, JSON or Parquet).",
    needs: "the file itself — you'll upload it on the source's page after it's created.",
    prompt: "register a file data source (kind:'csv' unless I say otherwise) named from my description using add_source, then call discover_source_schema for it. Report the registered source name when done.",
  },
  postgres: {
    label: "Postgres",
    desc: "a Postgres database table.",
    needs: "host, port (5432), database, schema (public), user, password, and the table.",
    prompt: "register a Postgres data source from the connection details I give (host, port, database, schema, user, password, table) using add_source with kind:'postgres' and a config object, then discover_source_schema. If I haven't given details, ask me for them.",
  },
  mysql: {
    label: "MySQL",
    desc: "a MySQL database table.",
    needs: "host, port (3306), database, user, password, and the table.",
    prompt: "register a MySQL data source from the connection details I give (host, port 3306, database, user, password, table) using add_source with kind:'mysql' and a config object. If I haven't given details, ask me for them.",
  },
  sqlserver: {
    label: "SQL Server",
    desc: "a Microsoft SQL Server table.",
    needs: "host, port (1433), database, schema (dbo), user, password, and the table.",
    prompt: "register a SQL Server data source from the connection details I give (host, port 1433, database, schema dbo, user, password, table) using add_source with kind:'sqlserver' and a config object. If I haven't given details, ask me for them.",
  },
  oracle: {
    label: "Oracle",
    desc: "an Oracle database table.",
    needs: "host, port (1521), service/database, user, password, and the table.",
    prompt: "register an Oracle data source from the connection details I give (host, port 1521, database/service, user, password, table) using add_source with kind:'oracle' and a config object. If I haven't given details, ask me for them.",
  },
  snowflake: {
    label: "Snowflake",
    desc: "a Snowflake warehouse table.",
    needs: "account, warehouse, database, schema, user, password.",
    prompt: "register a Snowflake data source from the connection details I give (account, warehouse, database, schema, user, password) using add_source with kind:'snowflake' and a config object. If I haven't given details, ask me for them.",
  },
  bigquery: {
    label: "BigQuery",
    desc: "a Google BigQuery dataset.",
    needs: "GCP project, dataset, and a service-account JSON.",
    prompt: "register a BigQuery data source from the details I give (project, dataset, service-account credentials JSON) using add_source with kind:'bigquery' and a config object. If I haven't given details, ask me for them.",
  },
  redshift: {
    label: "Redshift",
    desc: "an Amazon Redshift table.",
    needs: "host, port (5439), database, schema, user, password, and the table.",
    prompt: "register a Redshift data source from the connection details I give (host, port 5439, database, schema, user, password, table) using add_source with kind:'redshift' and a config object. If I haven't given details, ask me for them.",
  },
  mongo: {
    label: "Mongo",
    desc: "a MongoDB collection.",
    needs: "connection URI, database, and collection.",
    prompt: "register a MongoDB data source from the details I give (connection URI, database, collection) using add_source with kind:'mongo' and a config object. If I haven't given details, ask me for them.",
  },
  s3: {
    label: "S3",
    desc: "objects in an S3 bucket.",
    needs: "bucket, prefix/key, and region.",
    prompt: "register an S3 data source from the details I give (bucket, prefix/key, region) using add_source with kind:'s3' and a config object. If I haven't given details, ask me for them.",
  },
};
const DEFAULT_KIND = {
  label: "Data source",
  desc: "a file or a database your pipeline reads from.",
  needs: "the connection details for the source.",
  prompt: "Help me add a new data SOURCE. Ask me what kind (file/postgres/mysql/mongo/…), then register it with add_source and discover_source_schema.",
};

export const dataSourceGuide = {
  key: "data_source",
  title: "Cortex Guided · Add a data source",
  makeSteps(engine, ctx) {
    const { setStatus, chatStep, goPage, refreshPages } = engine;
    const kind = (ctx?.kind || "").toLowerCase();
    const g = KIND_GUIDE[kind] || DEFAULT_KIND;
    return [
      {
        title: `Add a ${g.label} source`,
        lines: [
          `This guide registers <b>${g.label}</b> — ${g.desc}`,
          `You'll need: <i>${g.needs}</i>`,
          "The assistant registers it live and discovers its schema. Press Next.",
        ],
        run: async () => { goPage("/data-sources"); setStatus(`Ready. Press Next to add a ${g.label} source.`); },
      },
      {
        title: `1 · Describe the ${g.label} source`,
        lines: [
          `Tell the assistant the details for your <b>${g.label}</b> source.`,
          `It needs: <i>${g.needs}</i>`,
          "It picks the right tool (add_source with the correct kind + config).",
        ],
        body: () => null,
        run: async () => {
          goPage("/data-sources");
          const before = await countSources();
          const ok = await chatStep(
            `Help me add a new ${g.label} data SOURCE to this project. ${g.prompt} Report the registered source name + discovered columns when done.`,
            async () => (await countSources()) > before,
            { totalMs: 240_000, statusFn: async (s) => { refreshPages(); return `registering ${g.label} source… (${await countSources()} sources, ${s}s)`; } },
          ).catch(() => false);
          refreshPages();
          setStatus(ok ? `✓ ${g.label} source registered (${await countSources()} total)` : "⚠ not yet — use the chat to give the connection details, or Next to retry");
        },
        autoAdvanceReady: () => false,
      },
      {
        title: "2 · Schema discovered",
        lines: [
          "The source's columns + types are now captured as its schema —",
          "ready to map into canonical <b>data elements</b> and feed a pipeline.",
        ],
        run: async () => { goPage("/data-sources"); refreshPages(); setStatus(`✓ done — ${await countSources()} source(s) in this project`); },
      },
    ];
  },
};
